import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Camera, ImagePlus, Trash2, Loader2, FileImage } from "lucide-react";
import { toast } from "sonner";

const MAX_CARGO = 10;

/**
 * ShipmentPhotoUploader — dual-purpose uploader for a shipment.
 *
 * • kind="document" → single sevk evrağı fotoğrafı (irsaliye/waybill).
 * • kind="cargo"    → up to 10 kargo/paket fotoğrafı, ızgara halinde.
 *
 * Every selected file is compressed client-side (max 1600px edge, JPEG 0.82)
 * before hitting `/api/shipments/{id}/photos/upload` so mobile shots don't
 * exhaust upload bandwidth.
 */
export default function ShipmentPhotoUploader({ shipmentId, kind = "cargo" }) {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const isDoc = kind === "document";
  const limit = isDoc ? 1 : MAX_CARGO;

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    if (!shipmentId) return;
    try {
      const { data } = await api.get(`/shipments/${shipmentId}/photos`);
      setPhotos((data || []).filter((p) => p.kind === kind));
    } catch (err) {
      // 404 during initial editor = shipment not yet saved — ignore silently
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [shipmentId]);

  const currentCount = photos.length;
  const canAddMore = currentCount < limit;

  const handleFiles = async (fileList) => {
    if (!shipmentId) {
      toast.error("Önce sevkiyatı kaydedin, sonra fotoğraf ekleyin");
      return;
    }
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;

    const remaining = limit - currentCount;
    if (files.length > remaining) {
      toast.warning(`En fazla ${remaining} fotoğraf daha ekleyebilirsiniz`);
    }
    const toUpload = files.slice(0, remaining);
    if (!toUpload.length) return;

    setBusy(true);
    let ok = 0, fail = 0;
    for (const f of toUpload) {
      try {
        const compressed = await compressImage(f);
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", compressed, compressed.name);
        const { data } = await api.post(
          `/shipments/${shipmentId}/photos/upload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        setPhotos((prev) => [...prev, data]);
        ok += 1;
      } catch (err) {
        fail += 1;
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
    }
    if (ok > 0) toast.success(`${ok} fotoğraf yüklendi${fail ? ` (${fail} başarısız)` : ""}`);
    setBusy(false);
    if (galleryRef.current) galleryRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const removePhoto = async (id) => {
    if (!window.confirm("Fotoğraf silinsin mi?")) return;
    try {
      await api.delete(`/shipments/${shipmentId}/photos/${id}`);
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      toast.success("Fotoğraf silindi");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const photoUrl = (id) => `${backendUrl}/api/shipments/${shipmentId}/photos/${id}/download`;

  return (
    <div className="space-y-2" data-testid={`shipment-photos-${kind}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase text-slate-500 font-mono flex items-center gap-1.5">
          <FileImage className="w-3.5 h-3.5" />
          {isDoc ? "Sevk Evrağı Fotoğrafı" : `Kargo Fotoğrafları (${currentCount}/${MAX_CARGO})`}
        </Label>
        {shipmentId && canAddMore && (
          <div className="flex gap-1.5">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid={`ship-photo-camera-${kind}`}
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              multiple={!isDoc}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid={`ship-photo-gallery-${kind}`}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => cameraRef.current?.click()}
              className="border-emerald-800 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-900/60 rounded-sm h-7 text-xs"
            >
              <Camera className="w-3.5 h-3.5 mr-1" /> Kamera
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => galleryRef.current?.click()}
              className="border-blue-800 bg-blue-950/30 text-blue-300 hover:bg-blue-900/60 rounded-sm h-7 text-xs"
            >
              <ImagePlus className="w-3.5 h-3.5 mr-1" /> Galeri
            </Button>
          </div>
        )}
      </div>

      {!shipmentId && (
        <div className="text-[10px] text-slate-500 font-mono border border-slate-800 bg-slate-950/40 rounded-sm px-2 py-1.5">
          Fotoğraf eklemek için önce sevkiyatı kaydedin.
        </div>
      )}

      {busy && (
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
          Fotoğraflar sıkıştırılıp yükleniyor…
        </div>
      )}

      {photos.length > 0 && (
        <div className={`grid gap-2 ${isDoc ? "grid-cols-1" : "grid-cols-3 sm:grid-cols-5"}`}>
          {photos.map((p) => (
            <div
              key={p.id}
              className="relative group border border-slate-800 rounded-sm overflow-hidden bg-slate-950 aspect-square"
              data-testid={`ship-photo-thumb-${p.id}`}
            >
              <img
                src={photoUrl(p.id)}
                alt={p.original_filename}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => removePhoto(p.id)}
                title="Sil"
                data-testid={`ship-photo-remove-${p.id}`}
                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-red-900/80 hover:bg-red-800 text-white rounded-sm p-1"
              >
                <Trash2 className="w-3 h-3" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[9px] text-slate-300 font-mono px-1.5 py-0.5 truncate">
                {(p.size / 1024).toFixed(0)} KB
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && shipmentId && (
        <div className="text-[10px] text-slate-500 font-mono">
          Henüz {isDoc ? "sevk evrağı" : "kargo"} fotoğrafı yok.
          {isDoc ? " Telefonun kamerasından çekebilir veya galeriden seçebilirsiniz." : ""}
        </div>
      )}
    </div>
  );
}
